import { useEffect, useState } from 'react';
import { listStocks } from '../lib/stocks';
import { fmtMarketCap, fmtNumber, rsiLabel, rsiStyle } from '../lib/format';
import type { Stock } from '../types/stock';

export default function Watchlist() {
  const [stocks, setStocks] = useState<Stock[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStocks()
      .then(setStocks)
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-12">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Watchlist</h1>
        <p className="mt-4 text-red-600 dark:text-red-400">Kunde inte hämta aktier: {error}</p>
      </div>
    );
  }

  if (stocks === null) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-12">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Watchlist</h1>
        <p className="mt-4 text-slate-500 dark:text-slate-400">Laddar...</p>
      </div>
    );
  }

  if (stocks.length === 0) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-12">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Watchlist</h1>
        <p className="mt-4 text-slate-600 dark:text-slate-400">
          Inga aktier ännu. Lägg till några via PocketBase-admin (collection:{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm dark:bg-slate-800">stocks</code>).
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Watchlist</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {stocks.length} aktier · uppdaterad varje timme
        </p>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="px-3 py-3">Ticker</th>
              <th className="px-3 py-3">Namn</th>
              <th className="px-3 py-3 text-right">Pris</th>
              <th className="px-3 py-3 text-right">P/E</th>
              <th className="px-3 py-3 text-right">Div.</th>
              <th className="px-3 py-3 text-right">Börsvärde</th>
              <th className="px-3 py-3 text-right" title="Relative Strength Index, 14 dagar">
                RSI(14)
              </th>
              <th className="px-3 py-3">Uppdaterad</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {stocks.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-3 py-3 font-mono font-medium text-slate-900 dark:text-white">
                  {s.ticker}
                </td>
                <td className="px-3 py-3 text-slate-700 dark:text-slate-300">
                  {s.name || '—'}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-900 dark:text-white">
                  {fmtNumber(s.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                  {s.currency && <span className="text-xs text-slate-500 dark:text-slate-400">{s.currency}</span>}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {fmtNumber(s.pe_ratio, { maximumFractionDigits: 1 })}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {s.dividend_yield !== undefined && s.dividend_yield !== 0
                    ? `${s.dividend_yield.toFixed(2)} %`
                    : '—'}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {fmtMarketCap(s.market_cap)}
                </td>
                <td className={`px-3 py-3 text-right tabular-nums ${rsiStyle(s.rsi_14)}`}>
                  {rsiLabel(s.rsi_14)}
                </td>
                <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                  {s.last_updated ? new Date(s.last_updated).toLocaleString('sv-SE') : '—'}
                  {s.last_error && (
                    <div className="mt-1 text-red-600 dark:text-red-400" title={s.last_error}>
                      fel: {s.last_error.slice(0, 40)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
        RSI(14) &gt; 70 = överköpt (röd), &lt; 30 = översåld (grön). Övrig tid = neutral.
      </p>
    </div>
  );
}
